"use client"

import { useId, useRef } from "react"
import styled from "@emotion/styled"
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form"
import Paperclip from "../../../icons/Paperclip"
import ImageIcon from "../../../icons/ImageIcon"
import CloseIcon from "../../../icons/CloseIcon"
import { colors } from "../../../theme/colors"
import { sizes } from "../../../theme/sizes"
import { breakpoints } from "../../../theme/mediaQueries"
import { StyledFieldError } from "../../forms/FormShell.styles"

const StyledWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
`

/* EGDE-579: the message is a flex child, so the wrapper's 8px gap stacks with
   the shared error's own 4px margin — cancel the difference so the text sits
   4px below the field, exactly like MUI's helper text under the inputs. */
const StyledUploadError = styled(StyledFieldError)`
  margin-top: calc(4px - ${sizes.spacing[8]}px);
`

const StyledTriggerButton = styled.button<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${sizes.spacing[8]}px;
  padding: 0 ${sizes.spacing[16]}px;
  /* EGDE-224: the file-upload field uses a DASHED boundary per Figma (distinct
     from the solid text inputs), with the paperclip attachment glyph. */
  border: 1px dashed
    ${(p) =>
      p.hasError ? colors.primitives.error500 : colors.primitives.neutral300};
  border-radius: 9999px;
  background-color: ${colors.light.bg.primary};
  cursor: pointer;
  /* EGDE-224: placeholder text is black, matching the other form fields. */
  color: ${colors.primitives.neutral600};
  /* EGDE-580: native buttons don't inherit the page font — without this the
     trigger label renders in the browser default (Arial) instead of Almoni. */
  font-family: inherit;
  font-size: 18px;
  line-height: 24px;
  text-align: start;

  /* EGDE-224: on the narrower desktop columns the upload label competes with the
     paperclip glyph for width, so it needs to be a touch smaller than the text
     fields (14px) to keep the longest label ("...הרכב הניזוק") fully readable
     at 1280px instead of truncating. */
  @media (min-width: ${breakpoints.lg}px) {
    font-size: 14px;
  }

  &:hover {
    border-color: ${colors.primitives.neutral400};
  }
`

const StyledTriggerLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${colors.primitives.neutral600};
`

/* EGDE-528: required marks are brand orange at rest, matching the text-field
   label asterisks. */
const StyledRequiredMark = styled.span`
  color: ${colors.primitives.primary500};
`

/* EGDE-238: uploaded files lay out as compact chips that wrap side by side
   (a horizontal row), not a stacked column. RTL-safe — `gap` and `flex-wrap`
   are direction-agnostic, so chips flow start→end (right→left in RTL). */
const StyledFileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${sizes.spacing[8]}px;
`

/* Filled-file chip — compact content-width pill (icon + truncated name +
   remove). Kept deliberately narrow (small padding/gaps + capped name width)
   so at least two fit side by side and wrap even on the narrowest mobile
   column (~290px), matching the Figma upload design. */
const StyledFileRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[4]}px;
  /* Cap each chip at half the row (minus the list gap) so at least two always
     sit side by side, even on a ~240px field column at 320px viewports — the
     name truncates via its inner ellipsis rather than the chip growing. */
  max-width: calc(50% - ${sizes.spacing[4]}px);
  padding: ${sizes.spacing[6]}px ${sizes.spacing[8]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: 9999px;
  color: ${colors.primitives.neutral600};
  font-size: 14px;
  line-height: 20px;
`

/* EGDE-238: per-chip glyph. Image uploads show a picture icon (not the generic
   paperclip) to match the Figma upload design; other file types keep the clip. */
const StyledPillIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${colors.primitives.neutral500};

  svg {
    width: 18px;
    height: 18px;
  }
`

/* `min-width: 0` + a small `max-width` lets the ellipsis kick in so a long
   filename truncates rather than stretching the chip — keeping chips narrow
   enough to sit several-per-row in a half-width column. */
const StyledFileName = styled.span`
  min-width: 0;
  max-width: 64px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/* Clickable file name (single-file replace) — reopens the picker. */
const StyledReplaceName = styled.button`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: start;
  color: ${colors.primitives.neutral600};
  /* EGDE-580: buttons don't inherit the page font. */
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
`

const StyledSingleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${sizes.spacing[8]}px;
  height: 48px;
  padding: 0 ${sizes.spacing[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: 9999px;
  color: ${colors.primitives.neutral600};
  font-size: 16px;
  line-height: 24px;
`

const StyledDeleteButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${colors.primitives.neutral500};

  &:hover {
    color: ${colors.primitives.neutral600};
  }
`

interface FormFileUploadProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  /** Default trigger text shown when nothing is selected. */
  label: string
  /** `accept` attribute, e.g. ".pdf,.doc,.docx" or "image/jpeg,image/png". */
  accept: string
  multiple?: boolean
  maxFiles?: number
  required?: boolean
  error?: string
  /** Chip glyph for uploaded files — "image" shows a picture icon, "file" the
   * paperclip. Defaults to "file". (EGDE-238) */
  iconVariant?: "file" | "image"
}

export default function FormFileUpload<T extends FieldValues>({
  control,
  name,
  label,
  accept,
  multiple = false,
  maxFiles = 6,
  required,
  error,
  iconVariant = "file",
}: FormFileUploadProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const ChipIcon = iconVariant === "image" ? ImageIcon : Paperclip

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const openPicker = () => inputRef.current?.click()

        const single = (multiple ? undefined : field.value) as
          | File
          | undefined
        const list: File[] = multiple
          ? Array.isArray(field.value)
            ? (field.value as File[])
            : []
          : []

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const picked = Array.from(e.target.files ?? [])
          if (picked.length === 0) return
          if (multiple) {
            field.onChange([...list, ...picked].slice(0, maxFiles))
          } else {
            field.onChange(picked[0])
          }
          // Allow re-selecting the same file after a delete.
          e.target.value = ""
        }

        const removeAt = (index: number) => {
          field.onChange(list.filter((_, i) => i !== index))
        }

        const canAddMore = !multiple ? !single : list.length < maxFiles

        return (
          <StyledWrapper>
            <input
              id={inputId}
              ref={inputRef}
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={handleChange}
              style={{ display: "none" }}
            />

            {/* Trigger: hidden once a single file is chosen, or while the
                multi list still has room. */}
            {canAddMore && (
              <StyledTriggerButton
                type="button"
                hasError={!!error}
                onClick={openPicker}
                aria-label={label}
              >
                <StyledTriggerLabel>
                  {label}
                  {required ? <StyledRequiredMark> *</StyledRequiredMark> : ""}
                </StyledTriggerLabel>
                <Paperclip />
              </StyledTriggerButton>
            )}

            {/* Single-file: clicking the name reopens the picker to replace. */}
            {!multiple && single && (
              <StyledSingleRow>
                <StyledReplaceName type="button" onClick={openPicker}>
                  {single.name}
                </StyledReplaceName>
                <StyledDeleteButton
                  type="button"
                  aria-label="remove file"
                  onClick={() => field.onChange(undefined)}
                >
                  <CloseIcon />
                </StyledDeleteButton>
              </StyledSingleRow>
            )}

            {/* Multi-file: one pill per uploaded file, each with a delete X. */}
            {multiple && list.length > 0 && (
              <StyledFileList>
                {list.map((file, index) => (
                  <StyledFileRow key={`${file.name}-${index}`}>
                    <StyledPillIcon>
                      <ChipIcon />
                    </StyledPillIcon>
                    <StyledFileName>{file.name}</StyledFileName>
                    <StyledDeleteButton
                      type="button"
                      aria-label="remove file"
                      onClick={() => removeAt(index)}
                    >
                      <CloseIcon />
                    </StyledDeleteButton>
                  </StyledFileRow>
                ))}
              </StyledFileList>
            )}

            {error && <StyledUploadError>{error}</StyledUploadError>}
          </StyledWrapper>
        )
      }}
    />
  )
}
